import { useCallback, useLayoutEffect, useState } from "react";

/**
 * 固定領域の実寸を初回は描画前に測り、以後のサイズ通知は次のフレームにまとめる。
 * 無効時・対象がない間・実寸0pxの間は、最後に計測した高さ（未計測ならinitialHeight）を保持する。
 */
export const useObservedHeight = <T extends HTMLElement>({
  initialHeight = 0,
  enabled = true,
}: { initialHeight?: number; enabled?: boolean } = {}) => {
  const [element, setElement] = useState<T | null>(null);
  const [height, setHeight] = useState(initialHeight);
  const ref = useCallback((node: T | null) => setElement(node), []);

  useLayoutEffect(() => {
    if (!enabled || !element) return;
    let active = true;
    let frame: number | null = null;
    let measuredHeight: number | undefined;
    const measure = () => {
      const nextHeight = element.getBoundingClientRect().height;
      if (nextHeight > 0 && nextHeight !== measuredHeight) {
        measuredHeight = nextHeight;
        setHeight((current) => (current === nextHeight ? current : nextHeight));
      }
    };
    const schedule = () => {
      if (!active || frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        if (active) measure();
      });
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(element, { box: "border-box" });
    window.addEventListener("resize", schedule);
    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [element, enabled]);

  return { ref, height };
};
